import useMediaQuery from "@mui/material/useMediaQuery";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { assetDomain } from "../../../../config/config";
import { posthogSignup } from "../../../../helpers/posthog_management";
import {
  sendTrackingEvent,
  signupTracking,
} from "../../../../helpers/tracking_management";
import {
  updateProfilePreviewDialog,
  updateSecondaryVisitorDialog,
} from "../../../../redux/actions/dialog_action";
import { applyJob } from "../../../../redux/actions/job_action";
import {
  checkEmailExistance,
  newResumeUpload,
  register,
} from "../../../../redux/actions/user_action";
import FormTextField from "../FormTextField/FormTextField";
import ResumeDropzone from "../ResumeDropzone/ResumeDropzone";
import {
  BodyTypographyStyled,
  CompleteButton,
  DialogActionsStyled,
  DialogBold,
  DialogContentStyled,
  DialogFormContainer,
  DialogInfoContainer,
  DialogInfoText,
  DialogMainStyled,
  DialogStyled,
  DialogSubtitle,
  DialogText,
  DialogTitleStyled,
  EmptyContainer,
  LabelStyled,
  LoadingButtonStyled,
  StyledLink,
} from "./styles";
import { AggregatedSignUpFormUserParams } from "./types";

const debounce = (func: any, delay: any) => {
  let timeoutId: string | number | NodeJS.Timeout;
  return (...args: any) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const BaseDialog = () => {
  const forms = ["Name", "Email", "Mobile Number", "Nationality"];
  const confettiGif = `${assetDomain}/gifs/confetti.gif`;
  const dispatch: any = useDispatch();
  const dialog = useSelector(
    (state: any) => state.dialog?.secondaryVisitorDialog
  );
  const externalJobUrl = useSelector(
    (state: any) => state.jobs?.job?.externalJobUrl
  );
  const signUpOrigin = useSelector((state: any) => state?.navbar?.signUpOrigin);
  const selectedCompany = useSelector(
    (state: any) => state?.companies?.selectedCompany
  );
  const selectedJob = useSelector((state: any) => state?.jobs?.job);
  const currentCompany =
    Object.keys(selectedJob).length > 0 ? selectedJob.company : selectedCompany;

  const resume = useSelector((state: any) => state.user?.resume);
  const selectedId = useSelector((state: any) => state.jobs?.selectedJobId);
  const job = useSelector((state: any) => state.jobs?.job);
  const tablet = useMediaQuery("(max-width:767px)");
  const [validProceed, setValidProceed] = useState<boolean>(false);
  const [complete, setComplete] = useState<boolean>(false);
  const [exist, setExist] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [userParams, setUserParams] = useState<AggregatedSignUpFormUserParams>({
    name: "",
    validName: true,
    email: "",
    validEmail: true,
    phone: "",
    validPhone: true,
    nationality: "",
    validNationality: false,
    password: "",
    bio: "",
    urlParams: "",
    signupMedium: "",
    signupSource: "",
    currentSalary: "",
  });

  const router = useRouter();
  const currentUrl = router.asPath;

  function handleCompleteProfile() {
    dispatch(updateSecondaryVisitorDialog(false)).then(() => {
      sendTrackingEvent({
        event: "CE_click-complete-your-profile-onboarding",
      });
      dispatch(updateProfilePreviewDialog(true));
    });
  }

  function closeDialog() {
    if (processing) {
      return;
    } else if (!success && !processing) {
      dispatch(updateSecondaryVisitorDialog(false));
    } else {
      dispatch(updateSecondaryVisitorDialog(false)).then(() => {
        location.reload();
      });
    }
  }

  const debouncedRegister = useCallback(
    debounce((params: object) => {
      dispatch(checkEmailExistance(params)).then((res: boolean) => {
        setExist(res);
      });
    }, 800),
    []
  );

  useEffect(() => {
    const utmCampaign = router.query?.utm_campaign
      ?.toString()
      ?.toLowerCase()
      ?.includes("hireme_june2023")
      ? "HireMe_June2023"
      : "";

    const urlParams = document.cookie
      .split("; ")
      .find((row) => row.startsWith("params="))
      ?.toString()
      .replace("params=", "");

    const { signupMedium, signupSource } = signupTracking(
      currentUrl,
      signUpOrigin,
      selectedJob,
      selectedCompany
    );

    setUserParams({
      ...userParams,
      bio: utmCampaign,
      urlParams: urlParams,
      signupMedium: signupMedium,
      signupSource: signupSource,
    });
  }, [router.query]);

  useEffect(() => {
    setValidProceed(false);
    if (
      userParams.validName &&
      userParams.validEmail &&
      userParams.validPhone &&
      userParams.validNationality &&
      !exist &&
      complete &&
      resume.length > 1
    ) {
      setValidProceed(true);
    }

    if (
      userParams.name &&
      userParams.email &&
      userParams.validEmail &&
      userParams.validName
    ) {
      debouncedRegister(userParams);
    }
  }, [userParams, resume, exist, complete]);

  const registerHandler = () => {
    setProcessing(true);

    if (success) {
      dispatch(updateSecondaryVisitorDialog(false));
    } else {
      // 1) Create the user account
      // 2) Submit resume
      // 3) Apply the selected job
      dispatch(register(userParams)).then((response) => {
        const { user } = response || {};

        Cookies.set("userId", user?.id);

        dispatch(
          newResumeUpload({
            resume: resume,
            state: "review",
            canCancel: false,
            user_resume: "", // Need to pass empty string to trigger first time upload CE
          })
        ).then(() => {
          posthogSignup(selectedJob, currentCompany, signUpOrigin, "email");

          if (externalJobUrl) {
            setSuccess(true);
            setProcessing(false);
            return window.open(externalJobUrl, "_blank");
          }
          dispatch(
            applyJob({
              jobId: selectedId,
              canCancel: false,
              origin: signUpOrigin,
              jobDiscovery: "individual_job_page",
            })
          ).then((response: any) => {
            sendTrackingEvent({
              event: "CE_click-next-visitor-apply-job-popup",
              "user-id": user?.id ?? null,
              "job-bucket-id": response.payload.jobBucketId,
              url: window.location.href,
              "job-title": job?.title ?? null,
              aggregator: true,
              "job-id": selectedId ?? null,
            });

            sendTrackingEvent({
              event: "CE_click-apply-aggregator-postsignup",
              "job-id": selectedId ?? null,
              "job-bucket-id": response.payload.jobBucketId,
              resume: true,
              logged_in: false,
              scraped: job?.scraped ? true : false,
              "job-title": job?.title ?? null,
            });

            setSuccess(true);
            setProcessing(false);
          });
        });
      });
    }
  };

  return (
    <DialogStyled open={dialog} onClose={closeDialog}>
      <DialogTitleStyled>
        {success ? (
          <DialogBold>Thank you for applying!</DialogBold>
        ) : (
          <Fragment>
            <DialogBold>
              You're just seconds away from making your job application!
            </DialogBold>
            <DialogSubtitle>
              Just fill these in and your application will be sent once your
              resume is approved.
            </DialogSubtitle>
          </Fragment>
        )}
      </DialogTitleStyled>
      {success ? (
        <DialogContentStyled>
          {tablet ? (
            <img
              src={confettiGif}
              alt="GIF"
              height={150}
              width={150}
              style={{ position: "absolute", bottom: "75%", left: "35%" }}
            />
          ) : (
            <img
              src={confettiGif}
              alt="GIF"
              height={280}
              width={280}
              style={{ position: "absolute", bottom: "440px", left: "280px" }}
            />
          )}
          {userParams.bio ? (
            <Fragment>
              <DialogText>
                We've received your response and will be reaching out to
                schedule a consultation with our recruiters as soon as possible.
              </DialogText>
              <DialogText>
                An account has also been created for you automatically with the
                email provided.
              </DialogText>
            </Fragment>
          ) : (
            <Fragment>
              <DialogMainStyled>Your account has been created</DialogMainStyled>
              <DialogText>
                Your <DialogBold>account has been created </DialogBold>
                with the email provided!
              </DialogText>
              <DialogText>
                Please note that your{" "}
                <DialogBold>job application will be sent out</DialogBold> to
                employers as soon as your resume is approved.
              </DialogText>

              <DialogInfoContainer>
                <DialogInfoText>
                  <DialogBold>Complete your profile</DialogBold> to strengthen
                  your application.
                </DialogInfoText>
                <DialogInfoText>
                  We've pre-filled info from your resume to help make it faster
                  and easier!
                </DialogInfoText>
              </DialogInfoContainer>
            </Fragment>
          )}
        </DialogContentStyled>
      ) : (
        <DialogFormContainer>
          <EmptyContainer height="16px" />
          {forms.map((form, index) => {
            return (
              <FormTextField
                key={`form-textfield-${index}`}
                name={form}
                userParams={userParams}
                setUserParams={setUserParams}
                exist={exist}
              />
            );
          })}

          <EmptyContainer>
            <BodyTypographyStyled sx={{ mt: "8px", mb: "18px", ml: "12px" }}>
              Upload Resume
            </BodyTypographyStyled>
            <ResumeDropzone complete={complete} setComplete={setComplete} />
          </EmptyContainer>

          <EmptyContainer sx={{ marginTop: "20px" }}>
            <LabelStyled disclaimer="true">
              By clicking “Next” below, you acknowledge that you have read and
              agreed to Hiredly’s{" "}
              <StyledLink href="/terms-and-conditions">
                Terms & Conditions
              </StyledLink>{" "}
              and <StyledLink href="/privacy-policy">Privacy Policy</StyledLink>
              .
            </LabelStyled>
          </EmptyContainer>
        </DialogFormContainer>
      )}

      <DialogActionsStyled>
        {success ? (
          <CompleteButton onClick={handleCompleteProfile}>
            Complete Your Profile
          </CompleteButton>
        ) : (
          <LoadingButtonStyled
            disabled={!validProceed}
            onClick={registerHandler}
            loading={processing}
          >
            {processing ? "" : "Next"}
          </LoadingButtonStyled>
        )}
      </DialogActionsStyled>
    </DialogStyled>
  );
};

export default BaseDialog;
